import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Upload, X, Save, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useSiteSettings, FONT_OPTIONS } from '../../context/SiteSettingsContext';
import { uploadImage, resolveImageUrl } from '../../lib/imageUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AppearanceSettings = () => {
  const { settings, refresh } = useSiteSettings();
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [optimizing, setOptimizing] = useState(false);

  useEffect(() => {
    setForm(settings);
  }, [settings]);

  const handleField = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const handleOptimizeImages = async () => {
    setOptimizing(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.post(`${API}/admin/migrate-images-to-storage`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const m = res.data.migrated || {};
      const total = Object.values(m).reduce((a, b) => a + b, 0);
      await refresh();
      toast.success(total > 0 ? `Optimized ${total} image(s) — your site is now faster!` : 'All images are already optimized.');
    } catch (error) {
      console.error('Error optimizing images:', error);
      toast.error('Failed to optimize images');
    } finally {
      setOptimizing(false);
    }
  };

  const handleImage = async (key, file) => {
    if (!file) return;
    try {
      const url = await uploadImage(file);
      handleField(key, url);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to upload image');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/site-settings`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await refresh();
      toast.success('Appearance updated — changes are live!');
    } catch (error) {
      console.error('Error saving appearance:', error);
      toast.error('Failed to save appearance settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8 max-w-3xl" data-testid="appearance-settings">
      <h2 className="text-3xl font-bold mb-8" style={{ fontFamily: 'var(--heading-font)' }} data-testid="appearance-title">
        Website Appearance
      </h2>

      <div className="space-y-8">
        {/* Performance */}
        <section className="bg-white border border-zinc-200 p-6 space-y-3" data-testid="image-optimize-section">
          <h3 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'var(--heading-font)' }}>
            <Zap size={20} /> Performance
          </h3>
          <p className="text-sm text-zinc-600">Move any images still stored in the database to fast cloud storage. Run this once after a new deployment to speed up your Classes &amp; Studios pages. It&apos;s safe to run anytime.</p>
          <button
            onClick={handleOptimizeImages}
            disabled={optimizing}
            className="bg-black text-white hover:bg-zinc-800 rounded-full px-6 py-3 font-bold transition-colors flex items-center gap-2 disabled:opacity-60"
            data-testid="optimize-images-btn"
          >
            <Zap size={18} /> {optimizing ? 'Optimizing…' : 'Optimize Images'}
          </button>
        </section>

        {/* Branding */}
        <section className="bg-white border border-zinc-200 p-6 space-y-4">
          <h3 className="text-xl font-bold" style={{ fontFamily: 'var(--heading-font)' }}>Branding</h3>

          <div>
            <Label htmlFor="site_name">Site Name (text logo / footer)</Label>
            <Input
              id="site_name"
              value={form.site_name || ''}
              onChange={(e) => handleField('site_name', e.target.value)}
              data-testid="site-name-input"
            />
          </div>

          <div>
            <Label>Header Logo (shown in the top navigation bar — PNG with transparency supported)</Label>
            {form.logo_url ? (
              <div className="relative inline-block mt-2 bg-zinc-100 p-3" data-testid="logo-preview">
                <img src={resolveImageUrl(form.logo_url)} alt="Logo" className="h-14 w-auto object-contain" />
                <button
                  onClick={() => handleField('logo_url', null)}
                  className="absolute -top-3 -right-3 bg-black text-white p-1.5 rounded-full hover:bg-zinc-700"
                  data-testid="remove-logo-btn"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <label
                htmlFor="logo-input"
                className="mt-2 flex items-center gap-2 cursor-pointer bg-zinc-50 hover:bg-zinc-100 border border-zinc-200 px-4 py-3 w-fit transition-colors"
                data-testid="logo-upload-label"
              >
                <Upload size={18} /> Upload logo
                <input id="logo-input" type="file" accept="image/*" className="hidden" onChange={(e) => handleImage('logo_url', e.target.files?.[0])} data-testid="logo-upload-input" />
              </label>
            )}
          </div>
        </section>

        {/* Hero */}
        <section className="bg-white border border-zinc-200 p-6 space-y-4">
          <h3 className="text-xl font-bold" style={{ fontFamily: 'var(--heading-font)' }}>Homepage Hero</h3>

          <div>
            <Label htmlFor="hero_title">Hero Title</Label>
            <Input id="hero_title" value={form.hero_title || ''} onChange={(e) => handleField('hero_title', e.target.value)} data-testid="hero-title-input" />
          </div>
          <div>
            <Label htmlFor="hero_subtitle">Hero Subtitle</Label>
            <Input id="hero_subtitle" value={form.hero_subtitle || ''} onChange={(e) => handleField('hero_subtitle', e.target.value)} data-testid="hero-subtitle-input" />
          </div>

          <div>
            <Label>Hero Title Image (replaces hero text — PNG with transparency supported)</Label>
            {form.hero_image_url ? (
              <div className="relative inline-block mt-2 bg-zinc-100 p-3" data-testid="hero-image-preview">
                <img src={resolveImageUrl(form.hero_image_url)} alt="Hero" className="max-h-28 w-auto object-contain" />
                <button
                  onClick={() => handleField('hero_image_url', null)}
                  className="absolute -top-3 -right-3 bg-black text-white p-1.5 rounded-full hover:bg-zinc-700"
                  data-testid="remove-hero-image-btn"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <label
                htmlFor="hero-image-input"
                className="mt-2 flex items-center gap-2 cursor-pointer bg-zinc-50 hover:bg-zinc-100 border border-zinc-200 px-4 py-3 w-fit transition-colors"
                data-testid="hero-image-upload-label"
              >
                <Upload size={18} /> Upload hero title image
                <input id="hero-image-input" type="file" accept="image/*" className="hidden" onChange={(e) => handleImage('hero_image_url', e.target.files?.[0])} data-testid="hero-image-upload-input" />
              </label>
            )}
          </div>

          <div>
            <Label>Hero Title Image Size</Label>
            <Select value={form.hero_image_scale || 'medium'} onValueChange={(v) => handleField('hero_image_scale', v)}>
              <SelectTrigger className="max-w-xs" data-testid="hero-image-scale-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="small">Small</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="large">Large</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-zinc-500 mt-1">Controls how large the hero title image appears on the homepage.</p>
          </div>

          <div>
            <Label>Hero Subtitle Size</Label>
            <Select value={form.hero_subtitle_scale || 'medium'} onValueChange={(v) => handleField('hero_subtitle_scale', v)}>
              <SelectTrigger className="max-w-xs" data-testid="hero-subtitle-scale-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="small">Small</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="large">Large</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-zinc-500 mt-1">Controls the size of the subtitle text below the hero title.</p>
          </div>

          <div>
            <Label htmlFor="hero_bg_url">Hero Background Image URL</Label>
            <Input id="hero_bg_url" value={form.hero_bg_url || ''} onChange={(e) => handleField('hero_bg_url', e.target.value)} placeholder="https://..." data-testid="hero-bg-url-input" />
            <label
              htmlFor="hero-bg-input"
              className="mt-2 flex items-center gap-2 cursor-pointer bg-zinc-50 hover:bg-zinc-100 border border-zinc-200 px-4 py-3 w-fit transition-colors"
              data-testid="hero-bg-upload-label"
            >
              <Upload size={18} /> Or upload an image
              <input id="hero-bg-input" type="file" accept="image/*" className="hidden" onChange={(e) => handleImage('hero_bg_url', e.target.files?.[0])} data-testid="hero-bg-upload-input" />
            </label>
            {form.hero_bg_url && (
              <div className="mt-3 h-40 w-full bg-cover bg-center border border-zinc-200" style={{ backgroundImage: `url('${resolveImageUrl(form.hero_bg_url)}')` }} data-testid="hero-bg-preview" />
            )}
          </div>
        </section>

        {/* Homepage Sections */}
        <section className="bg-white border border-zinc-200 p-6 space-y-4">
          <h3 className="text-xl font-bold" style={{ fontFamily: 'var(--heading-font)' }}>Homepage Sections</h3>
          <p className="text-xs text-zinc-500">Edit the descriptions shown in the three feature cards on the homepage.</p>
          <div>
            <Label htmlFor="home_classes_desc">Weekly Classes — Description</Label>
            <Textarea id="home_classes_desc" rows={3} value={form.home_classes_desc || ''} onChange={(e) => handleField('home_classes_desc', e.target.value)} data-testid="home-classes-desc-input" />
          </div>
          <div>
            <Label htmlFor="home_studio_desc">The Studio — Description</Label>
            <Textarea id="home_studio_desc" rows={3} value={form.home_studio_desc || ''} onChange={(e) => handleField('home_studio_desc', e.target.value)} data-testid="home-studio-desc-input" />
          </div>
          <div>
            <Label htmlFor="home_community_desc">Community — Description</Label>
            <Textarea id="home_community_desc" rows={3} value={form.home_community_desc || ''} onChange={(e) => handleField('home_community_desc', e.target.value)} data-testid="home-community-desc-input" />
          </div>
        </section>

        {/* Theme */}
        <section className="bg-white border border-zinc-200 p-6 space-y-4">
          <h3 className="text-xl font-bold" style={{ fontFamily: 'var(--heading-font)' }}>Theme</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="accent_color">Accent Color</Label>
              <div className="flex items-center gap-3 mt-1">
                <input
                  id="accent_color"
                  type="color"
                  value={form.accent_color || '#FFD700'}
                  onChange={(e) => handleField('accent_color', e.target.value)}
                  className="h-10 w-16 cursor-pointer border border-zinc-300"
                  data-testid="accent-color-input"
                />
                <Input
                  value={form.accent_color || ''}
                  onChange={(e) => handleField('accent_color', e.target.value)}
                  className="w-32"
                  data-testid="accent-color-hex-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="heading_font">Heading Font</Label>
              <Select value={form.heading_font} onValueChange={(v) => handleField('heading_font', v)}>
                <SelectTrigger data-testid="heading-font-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FONT_OPTIONS.map((f) => (
                    <SelectItem key={f.name} value={f.name}>{f.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </section>

        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-[var(--accent)] text-black hover:bg-[var(--accent-hover)] rounded-full px-8 py-3 font-bold transition-transform hover:scale-105 active:scale-95 flex items-center gap-2 disabled:opacity-60"
          data-testid="save-appearance-btn"
        >
          <Save size={20} />
          {saving ? 'SAVING...' : 'SAVE CHANGES'}
        </button>
      </div>
    </div>
  );
};

export default AppearanceSettings;
